/**
 * API Error Handler Utility
 * Handles API errors with user-friendly messages
 */

/**
 * Handle API errors with standardized user messages
 * @param {Error} error - The error object from API call
 * @param {Object} context - Vue component context (this)
 * @param {string} defaultMessage - Default error message if none found
 * @returns {string} User-friendly error message
 */
export function handleApiError(error, context, defaultMessage = 'Đã xảy ra lỗi') {
  const response = error.response;

  if (!response) {
    // Network error
    const message = 'Lỗi kết nối mạng. Vui lòng kiểm tra kết nối internet.';
    if (context && context.$bvToast) {
      context.$bvToast.toast(message, {
        title: 'Lỗi kết nối',
        variant: 'danger',
        solid: true,
      });
    }
    return message;
  }

  const status = response.status;
  const errorData = response.data;
  const message = errorData?.message || errorData?.error || defaultMessage;

  switch (status) {
    case 400:
      // Bad Request - validation errors
      if (context && context.$bvToast) {
        context.$bvToast.toast(message, {
          title: 'Dữ liệu không hợp lệ',
          variant: 'warning',
          solid: true,
        });
      }
      break;

    case 401:
      // Unauthorized - session expired
      if (context && context.$bvToast) {
        context.$bvToast.toast('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.', {
          title: 'Phiên hết hạn',
          variant: 'danger',
          solid: true,
        });
      }
      // Redirect to login after delay
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
      break;

    case 403:
      // Forbidden - access denied
      if (context && context.$bvToast) {
        context.$bvToast.toast(message || 'Bạn không có quyền truy cập tài nguyên này.', {
          title: 'Truy cập bị từ chối',
          variant: 'danger',
          solid: true,
        });
      }
      break;

    case 404:
      // Not Found
      if (context && context.$bvToast) {
        context.$bvToast.toast(message || 'Không tìm thấy tài nguyên yêu cầu.', {
          title: 'Không tìm thấy',
          variant: 'warning',
          solid: true,
        });
      }
      break;

    case 500:
      // Internal Server Error
      if (context && context.$bvToast) {
        context.$bvToast.toast('Lỗi máy chủ. Vui lòng thử lại sau.', {
          title: 'Lỗi máy chủ',
          variant: 'danger',
          solid: true,
        });
      }
      console.error('Server error:', errorData);
      break;

    case 503:
      // Service Unavailable
      if (context && context.$bvToast) {
        context.$bvToast.toast('Dịch vụ tạm thời không khả dụng. Vui lòng thử lại sau.', {
          title: 'Dịch vụ không khả dụng',
          variant: 'warning',
          solid: true,
        });
      }
      break;

    default:
      // Other errors
      if (context && context.$bvToast) {
        context.$bvToast.toast(message, {
          title: 'Lỗi',
          variant: 'danger',
          solid: true,
        });
      }
  }

  return message;
}

/**
 * Validate date range filters
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @param {Object} context - Vue component context
 * @returns {boolean} True if valid
 */
export function validateDateRange(startDate, endDate, context) {
  if (!startDate || !endDate) {
    if (context && context.$bvToast) {
      context.$bvToast.toast('Vui lòng chọn ngày bắt đầu và ngày kết thúc!', {
        title: 'Thiếu dữ liệu',
        variant: 'warning',
        solid: true,
      });
    }
    return false;
  }

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (start > end) {
    if (context && context.$bvToast) {
      context.$bvToast.toast('Ngày bắt đầu phải trước hoặc bằng ngày kết thúc!', {
        title: 'Ngày không hợp lệ',
        variant: 'warning',
        solid: true,
      });
    }
    return false;
  }

  return true;
}

/**
 * Handle loading state wrapper for async operations
 * @param {Function} asyncFn - Async function to execute
 * @param {Object} context - Vue component context with loading state
 * @param {string} loadingKey - Key for loading state (default: 'onLoading')
 * @returns {Promise} Result of async function
 */
export async function withLoading(asyncFn, context, loadingKey = 'onLoading') {
  try {
    context[loadingKey] = true;
    return await asyncFn();
  } finally {
    context[loadingKey] = false;
  }
}

export default {
  handleApiError,
  validateDateRange,
  withLoading,
};
